import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import AppShell from "../components/AppShell";
import { useAuth } from "../lib/auth";
import { ArrowLeft, CaretRight, CaretLeft, CheckCircle, Clock, ListBullets } from "@phosphor-icons/react";

/** Render Claude-generated markdown with light formatting */
function MarkdownLite({ body }) {
  const lines = body.split("\n");
  return (
    <div className="prose-mobility text-zinc-200 leading-relaxed text-lg">
      {lines.map((ln, i) => {
        if (/^### /.test(ln)) return <h3 key={i} className="text-white font-display mt-6 mb-2" style={{ fontSize: 18, fontWeight: 700 }}>{ln.replace(/^### /, "")}</h3>;
        if (/^## /.test(ln)) return <h2 key={i} className="text-white font-display mt-8 mb-3" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.015em" }}>{ln.replace(/^## /, "")}</h2>;
        if (/^# /.test(ln)) return <h2 key={i} className="text-white font-display mt-8 mb-3" style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em" }}>{ln.replace(/^# /, "")}</h2>;
        if (/^[-*] /.test(ln)) return <li key={i} className="list-disc ml-6 my-1">{ln.replace(/^[-*] /, "")}</li>;
        if (/^\d+\. /.test(ln)) return <li key={i} className="list-decimal ml-6 my-1">{ln.replace(/^\d+\. /, "")}</li>;
        if (ln.trim() === "") return <div key={i} className="h-3" />;
        return <p key={i} className="my-2">{ln}</p>;
      })}
    </div>
  );
}

export default function CourseLesson() {
  const { slug, lessonId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/courses/${slug}`);
    setCourse(data);
    if (user) {
      try {
        const { data: enrolls } = await api.get(`/courses/me/enrollments`);
        setEnrollment((enrolls || []).find((e) => e.course_slug === slug) || null);
      } catch (_) {}
    }
  };

  useEffect(() => { load(); }, [slug, lessonId, user?.id]); // eslint-disable-line

  const { lesson, prevLesson, nextLesson, moduleTitle } = useMemo(() => {
    if (!course) return {};
    const flat = course.modules.flatMap((m) => m.lessons.map((l) => ({ ...l, _module_title: m.title })));
    const idx = flat.findIndex((l) => l.id === lessonId);
    return {
      lesson: idx >= 0 ? flat[idx] : null,
      prevLesson: idx > 0 ? flat[idx - 1] : null,
      nextLesson: idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null,
      moduleTitle: idx >= 0 ? flat[idx]._module_title : "",
    };
  }, [course, lessonId]);

  const complete = async (advance = true) => {
    if (!user) { navigate("/login"); return; }
    setMarking(true);
    try {
      const { data } = await api.post(`/courses/${slug}/lessons/${lessonId}/complete`);
      setEnrollment(data);
      if (advance && nextLesson) navigate(`/courses/${slug}/lesson/${nextLesson.id}`);
    } catch (_) {}
    finally { setMarking(false); }
  };

  if (!course) return <AppShell><div className="py-20 text-center label-mono"><span className="spinner mr-2 align-middle" />LOADING LESSON...</div></AppShell>;
  if (!lesson) return <AppShell><div className="py-20 text-center label-mono">LESSON NOT FOUND</div></AppShell>;

  const done = enrollment?.completed_lessons?.includes(lessonId);
  const completedCount = enrollment?.completed_lessons?.length || 0;

  return (
    <AppShell>
      <div className="grid lg:grid-cols-12 gap-8" data-testid="course-lesson-page">
        <article className="lg:col-span-8">
          <Link to={`/courses/${slug}`} className="btn btn-ghost btn-sm mb-6" data-testid="lesson-back">
            <ArrowLeft size={12} weight="bold" /> Course outline
          </Link>

          <div className="label-mono mb-2" style={{ color: "#E60012" }}>
            {moduleTitle.toUpperCase()}
          </div>
          <h1 className="font-display text-white mb-4" style={{ fontSize: "clamp(28px, 4vw, 44px)", fontWeight: 800, letterSpacing: "-0.03em", lineHeight: 1.1 }} data-testid="lesson-title">
            {lesson.title}
          </h1>
          <div className="flex items-center gap-4 label-mono mb-8">
            <span className="flex items-center gap-1"><Clock size={10} /> {lesson.duration_min} MIN</span>
            <span className="flex items-center gap-1">{course.instructor.toUpperCase()}</span>
            {done && <span className="flex items-center gap-1" style={{ color: "#00E676" }}><CheckCircle size={10} weight="fill" /> COMPLETED</span>}
          </div>

          <MarkdownLite body={lesson.body_md} />

          {lesson.key_takeaways && lesson.key_takeaways.length > 0 && (
            <div className="ai-panel p-5 mt-10" data-testid="lesson-takeaways">
              <div className="label-mono mb-3 flex items-center gap-1.5" style={{ color: "#E60012" }}>
                <ListBullets size={11} weight="fill" /> KEY TAKEAWAYS
              </div>
              <ul className="space-y-2 text-zinc-100">
                {lesson.key_takeaways.map((t, i) => (
                  <li key={i} className="flex gap-2">
                    <span style={{ color: "#E60012" }}>·</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer nav */}
          <div className="flex flex-wrap gap-3 items-center justify-between mt-10 pt-6 border-t border-[#27272A]">
            <div className="flex gap-3">
              {prevLesson ? (
                <Link to={`/courses/${slug}/lesson/${prevLesson.id}`} className="btn btn-ghost btn-sm" data-testid="lesson-prev">
                  <CaretLeft size={12} weight="bold" /> Previous
                </Link>
              ) : <span />}
              {nextLesson && (
                <Link to={`/courses/${slug}/lesson/${nextLesson.id}`} className="btn btn-ghost btn-sm" data-testid="lesson-next">
                  Next <CaretRight size={12} weight="bold" />
                </Link>
              )}
            </div>
            <button onClick={() => complete(true)} disabled={marking} className="btn btn-primary btn-sm" data-testid="lesson-complete-btn">
              {marking ? <span className="spinner mr-2" /> : <CheckCircle size={12} weight="fill" />}
              {done ? "Marked complete" : nextLesson ? "Mark complete & next" : "Mark complete"}
            </button>
          </div>
        </article>

        <aside className="lg:col-span-4">
          <div className="card p-5 sticky top-5" data-testid="lesson-sidebar">
            <div className="label-mono mb-3" style={{ color: "#E60012" }}>COURSE OUTLINE</div>
            <h3 className="font-display text-white text-base mb-3" style={{ fontWeight: 700 }}>{course.title}</h3>
            <div className="mb-3">
              <div className="flex justify-between mb-1.5"><span className="label-mono">PROGRESS</span><span className="text-white text-xs">{completedCount}/{course.lesson_count}</span></div>
              <div className="h-1 bg-[#1A1A1D] overflow-hidden">
                <div className="h-full" style={{ width: `${(completedCount / Math.max(course.lesson_count, 1)) * 100}%`, background: "#00E676" }} />
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto -mx-2">
              {course.modules.map((m, mi) => (
                <div key={m.id} className="mb-3">
                  <div className="label-mono px-2 py-1" style={{ color: "#22D3EE" }}>{`MODULE ${String(mi + 1).padStart(2, "0")}`}</div>
                  {m.lessons.map((l) => {
                    const isCurrent = l.id === lessonId;
                    const isDone = enrollment?.completed_lessons?.includes(l.id);
                    return (
                      <Link
                        key={l.id}
                        to={`/courses/${slug}/lesson/${l.id}`}
                        className={`flex items-center gap-2 text-xs px-2 py-1.5 ${isCurrent ? "bg-[#1A1A1D] text-white" : "text-zinc-400 hover:text-white"}`}
                      >
                        {isDone ? <CheckCircle size={11} weight="fill" style={{ color: "#00E676" }} /> : <span className="w-2.5 h-2.5 border border-[#27272A]" />}
                        <span className="truncate">{l.title}</span>
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </AppShell>
  );
}
